/* eslint-disable no-lone-blocks */
/* eslint-disable react/no-array-index-key */
import { FC, useCallback, useEffect, useRef, useState } from 'react';

import { useRouter } from 'next/router';

import { destroyCookie, parseCookies, setCookie } from 'nookies';

import { useToast } from '~/common/providers/toast';
import { useAuth } from '~/common/providers/auth';
import { useMediaQuery } from '~/common/hooks/use-media-query';
import { UserSubscriptionTypesEnum } from '~/common/enums/user-subscription-types.enum';
import { Typography } from '~/common/components/Typograph';
import { SwitchToggle } from '~/common/components/SwitchToggle';
import { Skeleton } from '~/common/components/Skeleton';
import { SelectionBadge } from '~/common/components/SelectionBadge';
import { IDropdownOption } from '~/common/components/Dropdown/types';
import { api } from '~/common/api';

import {
  HomeFooter,
  HomeFooterContent,
  ProgressiveAccessNotice,
  PromptCategoryFilterContainer,
  PromptFiltersWrapper,
  PromptList,
  PromptListContainer,
  PromptSection,
  PromptSectionHeader,
} from './styles';
import { usePromptList } from '../../providers/prompt-list';
import { IPromptRequest } from '../../interfaces/i-prompt-request';
import { IPromptListItem } from '../../interfaces/i-prompt-list-item';
import { IPromptDetails } from '../../interfaces/i-prompt-details';
import {
  PromptTypesEnum,
  PromptTypesFormattedEnum,
} from '../../enums/prompt-types-enum';
import { UserPromptList } from '../../components/UserPromptList';
import { SubscriptionChangeModal } from '../../components/SubscriptionChangeModal';
import { PromptListHeader } from '../../components/PromptListTitle';
import { PromptFormModal } from '../../components/PromptFormModal/index.';
import { PromptApprovalModal } from '../../components/PromptApprovalModal';
import { OutOfAccessModal } from '../../components/OutOfAccessModal';
import { AdvancedFilters } from '../../components/AdvancedFiltersPopover';
import { AdminPromptList } from '../../components/AdminPromptList';
import { normalizePromptMediaFields } from '../../utils/normalize-prompt-media';

const PROMPT_LIST_SCROLL_COOKIE_KEY = '@PromptX.prompt-list-scroll-top';

const persistPromptListScroll = (scrollTop: number): void => {
  setCookie(null, PROMPT_LIST_SCROLL_COOKIE_KEY, String(scrollTop), {
    path: '/',
    maxAge: 60 * 60 * 6,
  });
};

const getPersistedPromptListScroll = (): number | null => {
  const cookies = parseCookies();
  const scrollTop = cookies[PROMPT_LIST_SCROLL_COOKIE_KEY];

  if (scrollTop === undefined) {
    return null;
  }

  const parsedScrollTop = Number(scrollTop);

  if (Number.isNaN(parsedScrollTop) || parsedScrollTop < 0) {
    return null;
  }

  return parsedScrollTop;
};

const clearPersistedPromptListScroll = (): void => {
  destroyCookie(null, PROMPT_LIST_SCROLL_COOKIE_KEY, { path: '/' });
};

export const PromptListPage: FC = () => {
  const currentYear = new Date().getFullYear();
  const router = useRouter();
  const pendingScrollRestore = useRef<number | null>(null);
  const hasRestoredScroll = useRef(false);
  const restoringScrollLoad = useRef(false);
  const { adminView, loggedIn, user } = useAuth();
  const isSmallerThan800px = useMediaQuery('(max-width: 800px)');
  const { showToast } = useToast();
  const isScreenSmallerThan350px = useMediaQuery('(max-width: 350px)');
  const [pendingPrompts, setPendingPrompts] = useState<IPromptListItem[]>([]);
  const [loadingPendingPrompts, setLoadingPendingPrompts] = useState(false);

  const [subscriptionChangeModalOpen, setSubscriptionChangeModalOpen] =
    useState<
      | 'new-subscription'
      | 'canceled-subscription-change'
      | 'canceled-subscription'
      | null
    >(null);

  const [forbiddenAccessModalOpen, setForbiddenAccessModalOpen] =
    useState<UserSubscriptionTypesEnum | null>(null);
  const [isPromptFormModalOpen, setIsPromptFormModalOpen] = useState(false);
  const [chaingApprovalState, setChaingApprovalState] =
    useState<IPromptListItem>(null);
  const [updatingPrompt, setUpdatingPrompt] = useState<IPromptDetails>(null);
  const {
    prompts,
    loading,
    restriction,
    category,
    advancedFilters,
    tier,
    hasMorePromptsToLoad,
    hasLoadedFirstCall,
    sortBy,
    handleChangeSortBy,
    loadAtBottom,
    changeTier,
    handleAdvancedFilterSelect,
    handleCategoryChange,
    handleSavePrompt,
    debounceSearchChange,
    search,
    handleDeletePrompt,
    approvePrompt,
    unapprovePrompt,
  } = usePromptList();

  const handleOpenForbiddenAccessModal = useCallback(
    (type: UserSubscriptionTypesEnum) => {
      setForbiddenAccessModalOpen(type);
    },
    []
  );

  const handleEditPrompt = useCallback(
    async (promptId: string) => {
      try {
        const { data } = await api.get<IPromptDetails>(`/prompts/${promptId}`);

        setUpdatingPrompt({
          ...normalizePromptMediaFields(data),
          categoryOptions: (data.categoryOptions || []).map((option) =>
            typeof option === 'string'
              ? option
              : ((option as IDropdownOption).value as string) ||
                ((option as any)?.id as string)
          ),
        });

        setIsPromptFormModalOpen(true);
      } catch {
        showToast({
          severity: 'error',
          title: 'Erro',
          description: 'Não foi possível carregar o prompt para edição.',
        });
      }
    },
    [showToast]
  );

  const handleNewPrompt = useCallback(() => {
    setUpdatingPrompt(null);
    setIsPromptFormModalOpen(true);
  }, []);

  const loadPendingPrompts = useCallback(async () => {
    if (
      !loggedIn ||
      user?.subscriptionAccessSource !== 'WHITELIST' ||
      user?.type === 'ADMIN'
    ) {
      setPendingPrompts([]);
      return;
    }

    setLoadingPendingPrompts(true);

    try {
      const { data } = await api.get<IPromptListItem[]>('/prompts/my-pending');
      setPendingPrompts(
        (data || []).map((prompt) => normalizePromptMediaFields(prompt, true))
      );
    } catch {
      showToast({
        severity: 'error',
        title: 'Erro',
        description:
          'Não foi possível carregar seus prompts aguardando aprovação.',
      });
    } finally {
      setLoadingPendingPrompts(false);
    }
  }, [loggedIn, showToast, user?.subscriptionAccessSource, user?.type]);

  const handleSavePromptWithAccessRefresh = useCallback(
    async (request: IPromptRequest, updatingId?: string) => {
      await handleSavePrompt(request, updatingId);
      await loadPendingPrompts();
    },
    [handleSavePrompt, loadPendingPrompts]
  );

  const handleDeletePromptWithAccessRefresh = useCallback(
    async (promptId: string) => {
      await handleDeletePrompt(promptId);
      await loadPendingPrompts();
    },
    [handleDeletePrompt, loadPendingPrompts]
  );

  const confirmApprovalChange = useCallback(async () => {
    const method = chaingApprovalState.approved
      ? unapprovePrompt
      : approvePrompt;

    await method(chaingApprovalState.id).then(() => {
      setChaingApprovalState(null);
    });
  }, [approvePrompt, unapprovePrompt, chaingApprovalState]);

  const togglePromptApproval = useCallback(async (prompt: IPromptListItem) => {
    setChaingApprovalState(prompt);
  }, []);

  useEffect(() => {
    if (router?.query?.['new-subscription'] === 'true') {
      setSubscriptionChangeModalOpen('new-subscription');
    }

    if (router?.query?.['canceled-subscription'] === 'true') {
      setSubscriptionChangeModalOpen('canceled-subscription');
    }

    if (router?.query?.['canceled-subscription-change'] === 'true') {
      setSubscriptionChangeModalOpen('canceled-subscription-change');
    }
  }, [router]);

  useEffect(() => {
    const element = document.getElementById('mainlayoutcontainer');

    if (!element) return;

    const persistScrollTop = (): void => {
      persistPromptListScroll(element.scrollTop);
    };

    const handleRouteChangeStart = (): void => {
      persistScrollTop();
    };

    const handleScroll = (): void => {
      const { scrollTop, scrollHeight, clientHeight } = element;

      persistScrollTop();

      if (
        scrollTop + clientHeight >= scrollHeight &&
        hasMorePromptsToLoad &&
        hasLoadedFirstCall &&
        !loading &&
        !restoringScrollLoad.current
      ) {
        loadAtBottom();
      }
    };

    element.addEventListener('scroll', handleScroll);
    router.events.on('routeChangeStart', handleRouteChangeStart);

    return () => {
      element.removeEventListener('scroll', handleScroll);
      router.events.off('routeChangeStart', handleRouteChangeStart);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasMorePromptsToLoad, hasLoadedFirstCall, loading, router.events]);

  useEffect(() => {
    if (pendingScrollRestore.current !== null || hasRestoredScroll.current) {
      return;
    }

    {
      restriction?.active && (
        <ProgressiveAccessNotice>
          <Typography.Label size="small" color="neutral100">
            Durante os primeiros 7 dias do seu acesso anual, parte do catálogo
            fica disponível para navegação. O catálogo completo desta etapa será
            liberado em{' '}
            <strong>
              {new Date(restriction.unlocksAt || '').toLocaleDateString(
                'pt-BR',
                {
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric',
                }
              )}
            </strong>
            .
          </Typography.Label>
        </ProgressiveAccessNotice>
      );
    }

    const savedScrollTop = getPersistedPromptListScroll();
    if (savedScrollTop === null) {
      hasRestoredScroll.current = true;
      return;
    }

    pendingScrollRestore.current = savedScrollTop;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (
      pendingScrollRestore.current === null ||
      hasRestoredScroll.current ||
      !hasLoadedFirstCall ||
      loading
    ) {
      return;
    }

    const savedPosition = pendingScrollRestore.current;
    const element = document.getElementById('mainlayoutcontainer');

    if (!element) {
      return;
    }

    const maxScrollableTop = Math.max(
      0,
      element.scrollHeight - element.clientHeight
    );

    if (savedPosition <= maxScrollableTop || !hasMorePromptsToLoad) {
      element.scrollTo({
        top: Math.min(savedPosition, maxScrollableTop),
        behavior: 'auto',
      });
      hasRestoredScroll.current = true;
      pendingScrollRestore.current = null;
      clearPersistedPromptListScroll();
      return;
    }

    if (restoringScrollLoad.current) {
      return;
    }

    restoringScrollLoad.current = true;
    loadAtBottom().finally(() => {
      restoringScrollLoad.current = false;
    });
  }, [
    hasLoadedFirstCall,
    loading,
    hasMorePromptsToLoad,
    prompts.length,
    loadAtBottom,
  ]);

  useEffect(() => {
    loadPendingPrompts();
  }, [loadPendingPrompts]);

  return (
    <>
      <SubscriptionChangeModal
        openFor={subscriptionChangeModalOpen}
        close={() => setSubscriptionChangeModalOpen(null)}
      />

      <OutOfAccessModal
        openFor={forbiddenAccessModalOpen}
        close={() => setForbiddenAccessModalOpen(null)}
      />

      <PromptApprovalModal
        open={!!chaingApprovalState}
        action={chaingApprovalState?.approved ? 'unapprove' : 'approve'}
        close={() => setChaingApprovalState(null)}
        confirm={confirmApprovalChange}
      />

      <PromptFormModal
        open={isPromptFormModalOpen}
        updating={updatingPrompt}
        save={async (request: IPromptRequest) => {
          await handleSavePromptWithAccessRefresh(
            request,
            updatingPrompt?.id
          ).finally(() => {
            setIsPromptFormModalOpen(false);
            setUpdatingPrompt(null);
          });
        }}
        close={() => {
          setIsPromptFormModalOpen(false);
          setUpdatingPrompt(null);
        }}
      />

      <PromptListContainer>
        <PromptListHeader
          handleNewPrompt={handleNewPrompt}
          handleSearchChange={debounceSearchChange}
          initialSearch={search}
        />

        {user?.subscriptionAccessSource === 'WHITELIST' && (
          <PromptSection>
            <PromptSectionHeader>
              <Typography.Label
                size="medium"
                color="neutral100"
                style={{ fontWeight: 'bold' }}
              >
                Seus prompts aguardando aprovação
              </Typography.Label>
              <Typography.Label size="small" color="neutral400">
                Você pode criar prompts para revisão dos administradores. Aqui
                aparecem apenas os prompts enviados por você e ainda não
                aprovados.
              </Typography.Label>
            </PromptSectionHeader>

            {loadingPendingPrompts ? (
              <PromptList>
                {Array.from({ length: 4 }).map((_, index) => (
                  <Skeleton key={`pending-${index}`} height="320px" width="264px" />
                ))}
              </PromptList>
            ) : pendingPrompts.length > 0 ? (
              <UserPromptList
                prompts={pendingPrompts}
                togglePromptApproval={togglePromptApproval}
                handleOpenForbiddenAccessModal={handleOpenForbiddenAccessModal}
                editPrompt={handleEditPrompt}
                deletePrompt={handleDeletePromptWithAccessRefresh}
              />
            ) : (
              <Typography.Label size="small" color="neutral400">
                Você ainda não tem prompts aguardando aprovação.
              </Typography.Label>
            )}
          </PromptSection>
        )}

        {loading ? (
          <PromptList>
            {Array.from({ length: 12 }).map((_, index) => (
              <Skeleton key={index} height="320px" width="264px" />
            ))}
          </PromptList>
        ) : (
          <>
            <PromptCategoryFilterContainer>
              <SelectionBadge
                key="all"
                title="Todos"
                selected={!category}
                onSelect={() => handleCategoryChange(null)}
              />

              {Object.entries(PromptTypesFormattedEnum).map(
                ([value, title]) => {
                  const selected = category === (value as PromptTypesEnum);

                  return (
                    <SelectionBadge
                      key={value}
                      title={title}
                      selected={selected}
                      onSelect={() =>
                        handleCategoryChange(value as PromptTypesEnum)
                      }
                    />
                  );
                }
              )}
            </PromptCategoryFilterContainer>

            <AdvancedFilters
              category={category}
              current={advancedFilters}
              onSelect={handleAdvancedFilterSelect}
            />

            <PromptFiltersWrapper>
              <SwitchToggle
                fullWidth={isSmallerThan800px}
                current={tier}
                onChange={changeTier}
                selectedTextColor="neutral1000"
                fixedItemWidth={isSmallerThan800px ? '100%' : '100px'}
                height="40px"
                switches={[
                  {
                    title: 'Todos',
                    key: 'all',
                  },
                  {
                    title: 'Grátis',
                    key: UserSubscriptionTypesEnum.FREE,
                  },
                  {
                    title: 'Premium',
                    key: UserSubscriptionTypesEnum.PREMIUM,
                  },
                ]}
              />

              <SwitchToggle
                fullWidth={isSmallerThan800px}
                current={sortBy}
                onChange={handleChangeSortBy}
                fixedItemWidth={
                  isSmallerThan800px
                    ? '100%'
                    : isScreenSmallerThan350px
                    ? '75px'
                    : '100px'
                }
                selectedTextColor="neutral1000"
                height="40px"
                switches={[
                  {
                    title: isScreenSmallerThan350px
                      ? '+ usados'
                      : 'Mais usados',
                    key: 'most-used',
                  },
                  {
                    title: isScreenSmallerThan350px
                      ? '+ recentes'
                      : 'Mais recentes',
                    key: 'newest',
                  },
                  {
                    title: isScreenSmallerThan350px
                      ? '+ curtidos'
                      : 'Mais curtidos',
                    key: 'likes',
                  },
                ]}
              />
            </PromptFiltersWrapper>

            {(prompts || []).length === 0 && !loading && (
              <Typography.Label size="medium" color="neutral100">
                Nenhum prompt encontrado com os filtros aplicados.
              </Typography.Label>
            )}

            {adminView ? (
              <AdminPromptList
                promtps={prompts}
                editPrompt={handleEditPrompt}
                deletePrompt={handleDeletePrompt}
                togglePromptApproval={togglePromptApproval}
              />
            ) : (
              <UserPromptList
                prompts={prompts}
                togglePromptApproval={togglePromptApproval}
                handleOpenForbiddenAccessModal={handleOpenForbiddenAccessModal}
                editPrompt={handleEditPrompt}
                deletePrompt={handleDeletePromptWithAccessRefresh}
              />
            )}

            <HomeFooter>
              <HomeFooterContent>
                <span>© {currentYear} PromptX</span>
                <span aria-hidden="true">•</span>
                <a href="/privacy-policy">Política de Privacidade</a>
                <span aria-hidden="true">•</span>
                <a href="/terms-of-service">Termos de Serviço</a>
              </HomeFooterContent>
            </HomeFooter>
          </>
        )}
      </PromptListContainer>
    </>
  );
};
